import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Alert, IconButton
} from '@mui/material';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import BookingInvoicePdf from './BookingInvoicePdf';
import { formatCurrency, parseCurrency } from '../../utils/currencyUtils';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const CustomLabel = ({ title }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
    {title}
  </Typography>
);

const InvoiceExportDialog = ({ open, onClose, bookingData }) => {
    const [invoiceData, setInvoiceData] = useState(null);

    // State để trì hoãn hiển thị PDF (fix lỗi trắng màn hình khi mở modal)
    const [showPreview, setShowPreview] = useState(false);

    // Đợi Dialog mở xong (300ms) mới render PDF
    useEffect(() => {
        if (open) {
            setShowPreview(false);
            const timer = setTimeout(() => setShowPreview(true), 300);
            return () => clearTimeout(timer);
        }
    }, [open]);

    useEffect(() => {
        if (bookingData && open) {
            let initialItems = [];

            // Hàm tạo ID ngẫu nhiên để PDF render không bị crash khi xoá
            const genId = () => Math.random().toString(36).substr(2, 9);

            // 1. LUÔN CHỈ LẤY từ "Các hạng mục thu tiền" (customer_charges) nếu có
            if (bookingData.customer_charges && bookingData.customer_charges.length > 0) {
                bookingData.customer_charges.forEach(charge => {
                    initialItems.push({
                        _id: genId(),
                        description: charge.title || 'Dịch vụ',
                        quantity: parseInt(charge.quantity) || 1,
                        unit_price: parseFloat(charge.unit_price) || 0,
                        total: (parseFloat(charge.unit_price) || 0) * (parseInt(charge.quantity) || 1)
                    });
                });
            } 

            // Format ngày đi và ngày xuất định dạng hiển thị
            const tripDate = bookingData.start_date ? new Date(bookingData.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'TBA';
            const issueDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

            setInvoiceData({
                ...bookingData,
                invoice_number: `INV-${bookingData.booking_code}`,
                invoice_date: issueDate,
                trip_date: tripDate,
                customer_name_display: bookingData.customer ? bookingData.customer.name : '',
                customer_address: '',
                customer_email: bookingData.customer?.phone || bookingData.new_customer_phone || '', 
                items: initialItems, 
                refund_amount: 0 
            });
        }
    }, [bookingData, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData(prev => ({ ...prev, [name]: value }));
  };

  const handleCurrencyChange = (e) => {
      const { name, value } = e.target;
      setInvoiceData(prev => ({ ...prev, [name]: parseCurrency(value) }));
  };

  // --- LOGIC XỬ LÝ ITEMS ---
  const handleItemChange = (index, field, value) => {
      setInvoiceData(prev => {
          const newItems = [...prev.items];
          let val = value;
          
          if (field === 'unit_price' || field === 'total') val = parseCurrency(value);
          else if (field === 'quantity') val = parseInt(value) || 0;

          newItems[index] = { ...newItems[index], [field]: val };

          // Tự động tính Total nếu đổi Qty hoặc Unit Price
          if (field === 'quantity' || field === 'unit_price') {
              const q = field === 'quantity' ? val : newItems[index].quantity;
              const p = field === 'unit_price' ? val : newItems[index].unit_price;
              newItems[index].total = q * p;
          }

          return { ...prev, items: newItems };
      });
  };

  // Tính tổng Subtotal để hiển thị (Total Amount sẽ trừ refund)
  const calculateSubtotal = () => {
      if (!invoiceData?.items) return 0;
      return invoiceData.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const handleAddItem = () => {
    if (!invoiceData) return;
    setInvoiceData(prev => ({
      ...prev,
      // Cấp ID mới cho dòng được thêm vào
      items: [...prev.items, { _id: Math.random().toString(36).substr(2, 9), description: '', quantity: 1, unit_price: 0, total: 0 }]
    }));
  };

  const handleRemoveItem = (index) => {
    if (!invoiceData) return;
    setInvoiceData(prev => {
      const newItems = [...prev.items];
      newItems.splice(index, 1);
      return { ...prev, items: newItems };
    });
  };

  if (!invoiceData) return null;

return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ bgcolor: '#007373', color: 'white', fontWeight: 'bold' }}>
        XUẤT INVOICE (HÓA ĐƠN)
      </DialogTitle>

      {/* CHIA LAYOUT LEFT (FORM) - RIGHT (PREVIEW) */}
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'row', gap: 3, height: '85vh', p: 2 }}>
        
        {/* CỘT TRÁI: FORM NHẬP LIỆU (Chiếm 45% chiều rộng) */}
        <Box sx={{ width: '45%', display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto', pr: 1 }}>
            
            {/* THÔNG TIN CHUNG */}
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Box flex={1}>
                    <CustomLabel title="Số Invoice" />
                    <TextField size="small" fullWidth name="invoice_number" value={invoiceData.invoice_number || ''} onChange={handleChange} />
                </Box>
                <Box flex={1}>
                    <CustomLabel title="Ngày xuất" />
                    <TextField size="small" fullWidth name="invoice_date" value={invoiceData.invoice_date || ''} onChange={handleChange} />
                </Box>
                <Box flex={1}>
                    <CustomLabel title="Ngày đi (Trip Date)" />
                    <TextField size="small" fullWidth name="trip_date" value={invoiceData.trip_date || ''} onChange={handleChange} />
                </Box>
            </Box>

            <Box>
                <CustomLabel title="Khách hàng / Công ty" />
                <TextField size="small" fullWidth name="customer_name_display" value={invoiceData.customer_name_display || ''} onChange={handleChange} />
            </Box>

            <Box>
                <CustomLabel title="Địa chỉ" />
                <TextField size="small" fullWidth name="customer_address" value={invoiceData.customer_address || ''} onChange={handleChange} />
            </Box>

            {/* DANH SÁCH DỊCH VỤ */}
            <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#007373', borderBottom: '1px solid #007373', pb: 0.5 }}>
                    CÁC HẠNG MỤC THANH TOÁN
                </Typography>
                
                {invoiceData.items.map((item, index) => (
                    // Dùng _id làm key thay vì index để React xử lý UI không bị lỗi khi xóa
                    <Box key={item._id || index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start', bgcolor: '#f9f9f9', p: 1, borderRadius: 1 }}>
                        <Box sx={{}} flex={3}>
                            <TextField size="small" fullWidth label="Mô tả" value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} sx={{ mb: 1 }} />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField size="small" type="number" label="S.Lượng" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} sx={{ width: '80px' }} />
                                <TextField size="small" label="Đơn giá" value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} sx={{ flex: 1 }} />
                            </Box>
                        </Box>
                        <IconButton color="error" onClick={() => handleRemoveItem(index)} sx={{ mt: 1 }}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                ))}
                
                <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={handleAddItem} sx={{ mt: 1, color: '#007373', borderColor: '#007373' }}>
                    Thêm dòng
                </Button>
            </Box>

            {/* HÀNG TỔNG CỘNG & HOÀN TIỀN */}
            <Box sx={{ display: 'flex', gap: 2, pt: 2, pb: 2, borderTop: '1px solid #ddd' }}>
                <Box flex={1}>
                    <CustomLabel title="Số tiền REFUND (nếu có)" />
                    <TextField size="small" fullWidth name="refund_amount" value={invoiceData.refund_amount || ''} onChange={handleChange} sx={{ '& input': { color: 'red' } }} />
                </Box>
                <Box flex={1}>
                    <CustomLabel title="TỔNG CỘNG:" />
                    <TextField size="small" name="total_amount" value={formatCurrency(calculateSubtotal() - (parseFloat(invoiceData.refund_amount) || 0))} InputProps={{ readOnly: true }} sx={{ marginBottom: '0px !important', '& input': { color: 'blue', fontWeight: "bold" } }} />
                </Box>
            </Box>

        </Box>

        {/* CỘT PHẢI: PREVIEW PDF (Chiếm 55% chiều rộng) */}
        <Box sx={{ width: '55%', height: '100%', border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
          
          {/* Cập nhật logic showPreview thay cho key={length} */}
          {showPreview ? (
              <PDFViewer width="100%" height="100%" style={{ borderWidth: 0 }}>
                <BookingInvoicePdf data={{ ...invoiceData }} />
              </PDFViewer>
          ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: '#f5f5f5', color: '#666' }}>
                  <Typography>Đang tải bản xem trước...</Typography>
              </Box>
          )}
          
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Button onClick={onClose} variant="outlined" color="inherit">Hủy</Button>
        <PDFDownloadLink
          document={<BookingInvoicePdf data={{ ...invoiceData }} />}
          fileName={`[Invoice]_${invoiceData.invoice_number}_${invoiceData.customer_name_display}.pdf`}
          style={{ textDecoration: 'none' }}
        >
          {({ loading }) => (
            <Button variant="contained" disabled={loading} sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, fontWeight: 'bold' }}>
              {loading ? 'Đang tạo...' : 'Tải xuống Invoice'}
            </Button>
          )}
        </PDFDownloadLink>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceExportDialog;